/**
 * H1 · যাচাইয়ের বিস্তার — `/provider/:id/verification` (audit finding 04)
 *
 * Transcribed from `design/Oprappo P0 Screens.dc.html`, frame H1 (360 × 680).
 *
 * Frame note: "Three things checked with the method named, two explicitly not checked.
 * Listing the gaps is what makes the badge worth trusting; a badge that implies
 * everything was verified transfers the provider's risk onto Oprappo."
 *
 * BOTH arrays are rendered. `verified` and `not_checked` come from the same payload and
 * neither is filtered, truncated or collapsed here — that is the whole mechanism of the
 * screen, and it is why `VerificationResponse` always carries both.
 *
 * ═══════════════════════════════════════════════════════════════════════════════════
 * WIRED — AND THE WIRING IS THE FIX, NOT A REFACTOR.
 * ═══════════════════════════════════════════════════════════════════════════════════
 * This screen used to read `./data.ts`, whose resolver was:
 *
 *     export function verificationFor(_id: string | undefined) { return VERIFICATION; }
 *
 * The id was never read. Every provider's sheet showed the one drawn provider's checks —
 * `/provider/shahin-cooling/verification` rendered রহিম ইলেকট্রিক's national-ID result,
 * phone check and shop visit under শাহীন কুলিং's name, under a যাচাইকৃত badge. That is a
 * CONFIDENT WRONG ANSWER about whether a stranger was checked, on a product whose whole
 * proposition is trust, and it gets worse the more the badge is believed.
 *
 * `GET /v1/providers/:id/verification` now supplies the shop's name, the badge, both
 * headings, both checklists, the mediation note and the link's destination.
 * `apps/api/src/provider/__tests__/routes.test.ts § two ids, two answers` is the test
 * that would have caught the original: it asks twice and requires the answers to differ.
 *
 * ── FIDELITY: NOTHING BELOW THIS LINE MOVED ─────────────────────────────────────
 * Every box, padding, weight and colour is the frame's, unchanged from the transcription
 * this file shipped with. Only where the strings COME FROM changed.
 * ─────────────────────────────────────────────────────────────────────────────────
 *
 * DEVIATIONS (unchanged, and still reported):
 *  · § 04's `Sheet` is not used: it is `padding:14px 15px 15px` with radio options and a
 *    Button; H1 is `18px 16px 16px` with two checklists and a note, and no action at all.
 *  · Each verified line is drawn as one sentence — `জাতীয় পরিচয়পত্র — নাম ও ছবি মিলেছে`.
 *    The contract splits it into `label_bn` and `method_bn`, so the em dash is the one
 *    thing this screen joins. Reported: either the API sends the line, or the separator
 *    is design, not code.
 *  · An EMPTY `not_checked` has no drawn treatment — the heading with nothing under it is
 *    not drawn either. The section is rendered only when the array has entries, and the
 *    missing copy is reported rather than invented.
 *  · The drawn date `১২ জুলাই ২০২৬-এ` arrives as `checked_at_bn`, a STORED sentence. When
 *    the server has none it sends null with `copy_status: 'copy_missing'` and this screen
 *    renders the slot empty — it does not compose Bangla from a timestamp.
 */
import { useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Badge } from '@oprappo/ui';
import type { VerificationCheck } from '@oprappo/contracts';
import { Frame, SAND_FRAME } from '../offering/chrome.js';
import { SurfaceStates } from '../../states/boundary.js';
import { useResource } from '../../data/resource.js';
import { fetchVerification, type ProviderVerification } from './api.js';

/** `M5 12l5 5 9-10`, 16px, `2.5px`, success — the tick beside a checked item. */
function Tick(): JSX.Element {
  return (
    <svg width={16} height={16} viewBox="0 0 24 24" fill="none" aria-hidden className="mt-[3px] flex-none">
      <path
        d="M5 12l5 5 9-10"
        stroke="#1E6B4A"
        strokeWidth={2.5}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/** `M6 6l12 12M18 6L6 18`, 16px, `2px`, amber-text — the cross beside a gap. */
function Cross(): JSX.Element {
  return (
    <svg width={16} height={16} viewBox="0 0 24 24" fill="none" aria-hidden className="mt-[3px] flex-none">
      <path d="M6 6l12 12M18 6L6 18" stroke="#8A5A0C" strokeWidth={2} strokeLinecap="round" />
    </svg>
  );
}

/** `label_bn — method_bn`, the dash being the frame's own separator. */
function line(c: VerificationCheck): string {
  return c.method_bn === null ? c.label_bn : `${c.label_bn} — ${c.method_bn}`;
}

/**
 * THE SCREEN IS A PURE FUNCTION OF THE PAYLOAD.
 *
 * The view takes what the route returned and renders it; the container below does the
 * loading. That split is what lets the fidelity tests render this screen from the same
 * `fixtures/api/provider.h1-verification.json` the API's own suite asserts its response
 * against, so the chain the visual gate depends on is checked end to end: drawn frame ==
 * fixture == API output == this markup.
 *
 * `null` means "nothing has arrived yet", and the view renders its chrome and no content
 * rather than substituting design data — which is precisely the habit that produced the
 * bug this file's header describes.
 */
export function H1VerificationView({ data }: { data: ProviderVerification | null }): JSX.Element {
  const navigate = useNavigate();

  return (
    <Frame className={`${SAND_FRAME} overflow-hidden`}>
      {/* The provider screen behind the sheet, at the drawn `opacity:.32`. */}
      <div className="p-[16px] opacity-[.32] shrink min-h-0 overflow-hidden" aria-hidden>
        <div className="font-anek font-semibold text-[18px] leading-[1.75] text-ink">
          {data?.provider_bn ?? ''}
        </div>
        <div className="bg-white border border-border h-[58px] box-content mt-[13px]" />
      </div>
      <div className="flex-1" />

      <div
        role="dialog"
        aria-label={data?.title_bn ?? ''}
        className="bg-white border-t border-border shadow-sheet px-[16px] pt-[18px] pb-[16px]"
      >
        <div className="flex justify-center mb-[15px]">
          <div className="w-[36px] h-[3px] bg-border" />
        </div>

        <div className="flex gap-[10px] items-center">
          {/*
            * No badge at all when the server sent none. A যাচাইকৃত badge over an empty
            * checklist is a claim nobody checked — the same class of untruth as showing
            * one provider's checks under another's name.
            */}
          {data?.badge_bn ? (
            <Badge labelBn={data.badge_bn} tone="success" className="flex-none" />
          ) : null}
          <div className="font-anek font-normal text-[13px] leading-[1.75] text-muted">
            {/*
              * `checked_at_bn`, never a composed date. Null when nobody has written the
              * Bangla for this provider's timestamp, and an empty slot is the honest
              * rendering of that — see this file's header.
              */}
            {data?.checked_at_bn ?? ''}
          </div>
          {/*
            * AND THE SENTENCE THAT STANDS WHERE THE BADGE WOULD BE.
            *
            * The server sets `unverified_bn` exactly when `badge_bn` is null (AUTHORED —
            * `@oprappo/contracts § PROVIDER_UNVERIFIED_BN`, listed for native review in
            * `docs/copy-review/W2-13.md`), so the two are never both drawn and the slot
            * is never empty. Without this the seeded shops ship অপ্রাপ্য কী যাচাই করেছে
            * over an empty list with nothing saying why — a trust heading with nothing
            * under it, which reads as a screen that failed rather than as a shop nobody
            * has checked. Those are different facts and the second one is the true one.
            */}
          {data?.unverified_bn ? (
            <div className="flex-1 font-anek font-normal text-[13px] leading-[1.75] text-muted">
              {data.unverified_bn}
            </div>
          ) : null}
        </div>

        <div className="font-anek font-semibold text-[19px] leading-[1.75] text-ink mt-[11px]">
          {data?.title_bn ?? ''}
        </div>

        <div className="mt-[13px] flex flex-col gap-[10px]">
          {(data?.verified ?? []).map((c) => (
            <div key={c.check_key} className="flex gap-[10px] items-start">
              <Tick />
              <div className="flex-1 font-anek font-normal text-[14.5px] leading-[1.75] text-ink2">
                {line(c)}
              </div>
            </div>
          ))}
        </div>

        {(data?.not_checked ?? []).length === 0 ? null : (
          <div className="border-t border-border mt-[15px] pt-[14px]">
            <div className="font-anek font-semibold text-[15.5px] leading-[1.75] text-ink">
              {data?.not_checked_title_bn ?? ''}
            </div>
            <div className="mt-[11px] flex flex-col gap-[9px]">
              {(data?.not_checked ?? []).map((c) => (
                <div key={c.check_key} className="flex gap-[10px] items-start">
                  <Cross />
                  <div className="flex-1 font-anek font-normal text-[14px] leading-[1.75] text-body">
                    {line(c)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="border border-border bg-sand px-[13px] py-[11px] mt-[14px]">
          <div className="font-anek font-normal text-[13px] leading-[1.75] text-body">
            {data?.mediation_bn ?? ''}
            {/*
              * THE LINK IS RENDERED ONLY WHEN THERE IS ONE, words and destination both.
              *
              * The destination is the SERVER's, so the sheet and the refund terms it
              * promises cannot drift apart — and the server withholds `mediation_link_bn`
              * and `mediation_route` together when the shop's domain has no active terms
              * (`apps/api/src/provider/sql.ts § GET_REFUND_POLICY_KEY`), which is every
              * shop until W2-09 seeds them. A `<button>` rendered anyway is a clickable
              * element with no label and nothing behind it: noise to a screen reader and
              * a dead tap target to everyone else. The note then ends without its
              * connector, which is why the API sends the line without the em dash.
              */}
            {data?.mediation_link_bn && data.mediation_route ? (
              <>
                {' '}
                <button
                  type="button"
                  onClick={() => {
                    if (data.mediation_route) navigate(data.mediation_route);
                  }}
                  className="font-anek font-medium text-link bg-transparent border-0 p-0 rounded-none"
                >
                  {data.mediation_link_bn}
                </button>
              </>
            ) : null}
          </div>
        </div>
      </div>
    </Frame>
  );
}

export function H1Verification(): JSX.Element {
  const { id } = useParams();
  /*
   * `id ?? ''` and NOT a fallback provider. An empty segment is refused by the route with
   * a 422 and reaches the screen as a state; substituting a default here is how a screen
   * ends up confidently showing somebody else's verification, which is the whole bug.
   */
  const load = useCallback((signal: AbortSignal) => fetchVerification(id ?? '', signal), [id]);
  const { data, error } = useResource(load, `provider-verification:${id ?? ''}`);

  return (
    <SurfaceStates surface="transaction" error={error ?? undefined}>
      <H1VerificationView data={data} />
    </SurfaceStates>
  );
}
